// Feature access configuration for freemium/premium model
export type FeatureKey = 
  // Free features
  | 'basicChartEditor'
  | 'annotationsVisibility'  // Show annotations but disable editing
  | 'localSave'  // Local only, no cloud sync
  | 'gigMode'
  | 'setlistManager'  // Limited: 1 setlist, 5 songs max
  // Premium features  
  | 'jamSession'  // All jam session features
  | 'annotationsEditing'  // Edit annotations
  | 'cloudSync'
  | 'pdfExport'  // Export charts to PDF
  | 'advancedExport'  // MIDI export and other advanced formats
  | 'bandWorkspace'  // Shared setlists/charts
  | 'chartTemplates'  // AI Jazz Fill & templates
  | 'unlimitedSetlists'  // Unlimited setlists and songs
  | 'prioritySupport';

export type SubscriptionPlan = 'free' | 'premium';

export interface FeatureConfig {
  key: FeatureKey;
  name: string;
  description: string;
  plans: SubscriptionPlan[];
  priority: 'core' | 'high' | 'medium' | 'viral';
}

export const FEATURES: Record<FeatureKey, FeatureConfig> = {
  // FREE FEATURES
  basicChartEditor: {
    key: 'basicChartEditor',
    name: 'Chart Editor',
    description: 'Create and edit chord charts',
    plans: ['free', 'premium'],
    priority: 'core'
  },
  annotationsVisibility: {
    key: 'annotationsVisibility',
    name: 'View Annotations',
    description: 'See measure annotations (editing requires premium)',
    plans: ['free', 'premium'],
    priority: 'core'
  },
  localSave: {
    key: 'localSave', 
    name: 'Local Save',
    description: 'Save charts to your device only',
    plans: ['free', 'premium'],
    priority: 'core'
  },
  gigMode: {
    key: 'gigMode',
    name: 'Gig Mode',
    description: 'Performance-ready display',
    plans: ['free', 'premium'],
    priority: 'core'
  },

  // PREMIUM FEATURES (with free tier limits)
  jamSession: {
    key: 'jamSession',
    name: 'Jam Sessions',
    description: 'Real-time collaboration (Free: 2 persons max, Premium: unlimited)',
    plans: ['free', 'premium'],
    priority: 'high'
  },
  annotationsEditing: {
    key: 'annotationsEditing',
    name: 'Edit Annotations',
    description: 'Add and edit measure annotations',
    plans: ['premium'],
    priority: 'high'
  },
  cloudSync: {
    key: 'cloudSync',
    name: 'Cloud Sync',
    description: 'Access charts anywhere, any device',
    plans: ['premium'],
    priority: 'high'
  },
  pdfExport: {
    key: 'pdfExport',
    name: 'PDF Export',
    description: 'Print-ready PDF charts for gigs and rehearsals',
    plans: ['premium'],
    priority: 'high'
  },
  advancedExport: {
    key: 'advancedExport',
    name: 'Advanced Export',
    description: 'MIDI and other professional formats',
    plans: ['premium'],
    priority: 'medium'
  },
  bandWorkspace: {
    key: 'bandWorkspace',
    name: 'Band Workspace',
    description: 'Shared setlists and charts across your band',
    plans: ['premium'],
    priority: 'viral'
  },
  chartTemplates: {
    key: 'chartTemplates',
    name: 'Chart Templates & AI',
    description: 'Jazz standards, AI Jazz Fill, and practice lessons',
    plans: ['premium'],
    priority: 'medium'
  },
  setlistManager: {
    key: 'setlistManager',
    name: 'Setlist Manager',
    description: 'Organize your repertoire and setlists (Free: 1 setlist, 5 songs max)',
    plans: ['free', 'premium'],
    priority: 'medium'
  },
  unlimitedSetlists: {
    key: 'unlimitedSetlists',
    name: 'Unlimited Setlists',
    description: 'Create unlimited setlists with unlimited songs',
    plans: ['premium'],
    priority: 'high'
  },
  prioritySupport: {
    key: 'prioritySupport',
    name: 'Priority Support',
    description: 'Get help when you need it',
    plans: ['premium'],
    priority: 'medium'
  }
};

// Helper functions
export const getFreeFeatures = (): FeatureKey[] => 
  Object.values(FEATURES)
    .filter(f => f.plans.includes('free'))
    .map(f => f.key);

export const getPremiumFeatures = (): FeatureKey[] =>
  Object.values(FEATURES)
    .filter(f => f.plans.includes('premium') && !f.plans.includes('free'))
    .map(f => f.key);

export const getFeaturesByPriority = (priority: FeatureConfig['priority']): FeatureKey[] =>
  Object.values(FEATURES)
    .filter(f => f.priority === priority)
    .map(f => f.key);